import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from '@Redux/type';
import { AppState } from '@Redux/store';
import { GoogleMapZone } from './google-map.component.styled';
import ClubMarkerComponent from './club-marker.component';
import { Map, useMap } from '@vis.gl/react-google-maps';
import { ClubView } from '@Selectors/federation/view/club.view';
import { PlayerView } from '@Selectors/federation/view/player.view';
import Repeater from '@Utils/Repeater';
import { Badge, Card, CardBody, CardLink, CardText, CardTitle, Col } from 'reactstrap';
import ReactCountryFlag from 'react-country-flag';
import { ClubCategoryView } from '@Selectors/federation/view/club-category.view';
import { ClubWebsiteView } from '@Selectors/federation/view/club-website.view';
import PlayerMarkerComponent from '@Components/federation/player-marker.component';
import { container } from '@Container';
import { UserPreferenceAction } from '@Redux/actions';
import { TYPES } from '@Types';
import { FormattedMessage } from 'react-intl';
import { defaultMessages } from '@I18n/default';

interface IGoogleMapComponentProps {
  clubs: ClubView[];
  categories: ClubCategoryView[];
  players: PlayerView[];
}

const createBounds = (map: google.maps.Map | null, clubs: ClubView[]) => {
  if (!map) return;

  if (clubs.length < 2) return;

  const bounds = new google.maps.LatLngBounds();
  clubs.forEach((club) => {
    bounds.extend(new google.maps.LatLng(club.latitude, club.longitude));
  });
  map.fitBounds(bounds);
};

const GoogleMapComponent: React.FC<Props> = (
  {
    clubs,
    categories,
    player,
    isGuest,
    saveLocation,
    deleteLocation,
    players }
) => {
  const center = { lat: 48.853, lng: 2.35 };
  const map = useMap();

  React.useEffect(() => {
    createBounds(map, filteredClubs);
  }, [map, clubs]);

  const [clubId, setClubId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [location, setLocation] = useState(player?.location ?? null);

  React.useEffect(() => {
    setLocation(player?.location ?? null);
  }, [player?.location]);

  const filteredClubs = useMemo(() => clubs.filter(c => !categoryId || c.categories.some(category => category.id === categoryId)), [clubs, categoryId]);

  const centerMarker = (club: ClubView) => {
    if (!map) return;
    map.setCenter({ lat: club.latitude, lng: club.longitude });
    setClubId(club.id);
  };

  const closeCenter = () => {
    setClubId('');
  };

  const handleLocationClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(success, error);
    } else {
      alert('Geolocation not supported');
    }
  };

  const success = (position: any) => {
    const latitude: number = position.coords.latitude;
    const longitude: number = position.coords.longitude;
    setLocation({ lat: latitude, lng: longitude });
    console.log(`Latitude: ${latitude}, Longitude: ${longitude}`);

    if (!map) return;
    map.setCenter({ lat: latitude, lng: longitude });
    map.setZoom(8);
  };

  const error = () => {
    alert('Unable to retrieve your location');
  };

  const resetLocation = (playerId: string) => {
    deleteLocation(playerId);
    setLocation(null);
  }

  return (
    <GoogleMapZone className={'row'}>
      <Col xs={12} md={9}>
        <Map defaultCenter={center} defaultZoom={4} mapId={process.env.MAP_ID ?? ''}>
          {filteredClubs.map((club) => {
            return (
              <ClubMarkerComponent key={club.id}
                                   club={club}
                                   selected={clubId === club.id}
                                   onClick={centerMarker}
                                   onClose={closeCenter}
              />
            );
          })}
          <Repeater data={players} indexPrefix="player">
            {
              (player: PlayerView) => (
                <PlayerMarkerComponent playerName={player?.name ?? 'Player'}
                                       location={{ lat: player.latitude, lng: player.longitude }} />
              )
            }
          </Repeater>
          {location && (
            <PlayerMarkerComponent playerName={player?.name ?? 'Me'} location={location} />
          )}
        </Map>
      </Col>

      <Col xs={12} md={3} className={'list'}>
        <div>
          <h3><FormattedMessage {...defaultMessages.locationCenter} /></h3>

          {!location && (
            <button className={'btn btn-age btn-lg center'} onClick={handleLocationClick}><i
              className={'fa fa-location'}></i> <FormattedMessage {...defaultMessages.locationDisplayLocation} />
            </button>
          )}
          {location && (
            <button className={'btn btn-age btn-lg center'} onClick={handleLocationClick}><i
              className={'fa fa-location'}></i> <FormattedMessage {...defaultMessages.locationDisplayRefresh} />
            </button>
          )}

          {location && !isGuest && player && (
            <>
              <p><FormattedMessage {...defaultMessages.locationShareDescription} /></p>
              <p><small><FormattedMessage {...defaultMessages.locationShareInfo} /></small></p>

              <button className='btn btn-age' onClick={() => saveLocation(player.id, location)}>
                <i className='fa fa-share-alt'></i> <FormattedMessage {...defaultMessages.formShare} />
              </button>
              <button className='btn btn-age' onClick={() => resetLocation(player.id)}>
                <i className='fa fa-trash'></i> <FormattedMessage {...defaultMessages.formDelete} />
              </button>
            </>
          )}
        </div>
        <hr />
        {categories.length > 0 && (
          <>
            <div className={'mt-2'}>
              <h3>Filtrer par jeu :</h3>
              <div className={'club-category'}>
                <Repeater data={categories} indexPrefix="club">
                  {
                    (item: any) => (
                      <Badge className={'m-1 cursor-pointer'} color={categoryId === item.id ? 'success' : 'primary'}
                             onClick={() => setCategoryId(item.id)}>{item.name}</Badge>
                    )
                  }
                </Repeater>
              </div>
            </div>
            <hr />
          </>
        )}
        {filteredClubs.length > 0 && (
          <div className={'mt-2'}>
            <h3>Clubs :</h3>
            <div>
              <Repeater data={filteredClubs} indexPrefix="club">
                {
                  (item: ClubView) => (
                    <Card className={clubId === item.id ? 'selected' : ''} onClick={() => centerMarker(item)}>
                      <CardBody>
                        <CardTitle><ReactCountryFlag countryCode={item.country} /> {item.name}</CardTitle>
                        <CardText>
                          {item.notes}
                          <p><u><b>Location:</b></u> {item.location}</p>
                          <p><u><b>Contact:</b></u> {item.contact}</p>
                          <p><u><b>Members:</b></u> {item.members}</p>
                          <p><u><b>Meetings:</b></u> {item.meets}</p>
                          <Repeater data={item.categories} indexPrefix="website">
                            {
                              (category: ClubCategoryView) => (
                                <Badge className={'m-1 cursor-pointer'} color={categoryId === category.id ? 'success' : 'primary'}
                                       onClick={() => setCategoryId(category.id)}>{category.name}</Badge>
                              )
                            }
                          </Repeater>
                        </CardText>
                        <Repeater data={item.websites} indexPrefix="website">
                          {
                            (website: ClubWebsiteView) => (
                              <>
                                {website.link.includes('http') &&
                                  <CardLink href={website.link} target={'_blank'}>{website.link}</CardLink>}
                                {!website.link.includes('http') && <CardLink href={'#'}>{website.link}</CardLink>}
                              </>
                            )
                          }
                        </Repeater>
                      </CardBody>
                    </Card>
                  )
                }
              </Repeater>
            </div>
          </div>
        )}
      </Col>
    </GoogleMapZone>
  );
};

const mapStateToProps = (state: AppState, props: IGoogleMapComponentProps) => ({
  clubs: props.clubs,
  players: props.players,
  categories: props.categories,
  isGuest: state.userPreference.entity?.isGuest ?? true,
  player: state.userPreference.entity ?? null
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  const userAction = container.get<UserPreferenceAction>(TYPES.Actions.User.UserPreferenceAction);
  return {
    saveLocation: (playerId: string, location: google.maps.LatLngLiteral) => {
      dispatch(userAction.saveLocation(playerId, location));
    },
    deleteLocation: (playerId: string) => dispatch(userAction.deleteLocation(playerId)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(GoogleMapComponent);

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> &
  IGoogleMapComponentProps
